import { Controller } from '@hotwired/stimulus';
import { cable } from '@hotwired/turbo-rails';

export default class extends Controller {
  static values = { stream: String };

  #subscribing = null;
  #refreshing = false;
  #refreshQueued = false;

  // Mounted on the frame itself - Turbo replaces only its contents, so the subscription and
  // the queue survive a refresh.
  connect() {
    this.#subscribing = cable.subscribeTo(
      { channel: 'BeaconChannel', signed_stream_name: this.streamValue },
      {
        connected: () => (this.element.dataset.beaconConnected = ''),
        disconnected: () => delete this.element.dataset.beaconConnected,
        received: () => this.refresh(),
      },
    );
  }

  // Stimulus never calls `connect` again before `disconnect`, so awaiting the promise stored
  // by `connect` always unsubscribes the matching subscription, even one still pending.
  async disconnect() {
    const subscription = await this.#subscribing;
    subscription.unsubscribe();
  }

  // A background refresh must never navigate the page, so the response is dropped instead of
  // reaching `break-frames.js`. Preventing it also keeps Turbo from throwing before `loaded`
  // settles, which would wedge the queue.
  ignoreMissingFrame(event) {
    // Nested frames bubble their own failures through here and keep the regular handling.
    if (event.target !== this.element) return;

    event.preventDefault();
    event.stopPropagation();
  }

  // Loading the frame aborts an in-flight request, so refreshes are serialized and a
  // single trailing run covers all beacons that arrived meanwhile.
  async refresh() {
    if (this.#refreshing) {
      this.#refreshQueued = true;
      return;
    }

    this.#refreshing = true;
    try {
      await this.#load();
    } finally {
      this.#refreshing = false;
    }

    if (this.#refreshQueued) {
      this.#refreshQueued = false;
      this.refresh();
    }
  }

  // `loaded` settles on success, HTTP failure, prevented render and network error alike,
  // so a failed fetch cannot wedge the frame.
  #load() {
    if (this.element.src) return this.element.reload();

    this.element.src = window.location.href;
    return this.element.loaded;
  }
}
